import type { FieldProvider } from '@atlassian/jira-common-rest/src/internal/simplified/1.0/projects/id/issuetypes/id/types.tsx';
import { toFieldUuid } from '@atlassian/jira-shared-types/src/general.tsx';
import type {
	Field,
	HiddenIn,
	HiddenInConfiguration,
	AvailabilityContext,
	DisplayConditions,
	Availability,
} from '../../types/layout-items.tsx';
import type {
	FieldItemFragment as GraphqlFieldItem,
	FieldItemFragment_availability as GraphqlFieldAvailability,
	FieldItemFragment_availability_isHiddenIn_JiraIssueLayoutFieldConfigurationHiddenInLayoutOwner_fieldConfigs as GraphqlFieldConfig,
} from '../fragments/__generated_apollo__/FieldItemFragment';
import type { FieldItemProviderFragment as GraphqlFieldItemProvider } from '../fragments/__generated_apollo__/FieldItemProviderFragment';
import { transformFieldOperationsAdapter } from './field-operations.tsx';
import { normalizeOptions } from './options.tsx';
import type { FieldTransformer, Transformers } from './types.tsx';

/**
 * Transform field provider data from GraphQL into a SWIFT compatible {@link FieldProvider} object.
 */
const transformProvider = (provider?: GraphqlFieldItemProvider | null): FieldProvider => {
	const { key: providerKey, name: providerName } = provider || { key: '', name: '' };

	return {
		key: providerKey,
		name: providerName,
	};
};

const transformIsHiddenIn = (
	isHiddenIn: GraphqlFieldAvailability['isHiddenIn'],
): HiddenIn[] | null => {
	if (isHiddenIn == null) {
		return null;
	}

	const hiddenIn: HiddenIn[] = [];

	const transformFieldConfig = ({
		id,
		configName: name,
	}: GraphqlFieldConfig): HiddenInConfiguration => ({
		id,
		name,
	});

	for (const configuration of isHiddenIn) {
		if (configuration.__typename === 'JiraIssueLayoutFieldConfigurationHiddenInGlobal') {
			const { fieldConfigs } = configuration;

			hiddenIn.push({
				fieldConfigs: (fieldConfigs || []).map(transformFieldConfig),
			});
		}

		if (configuration.__typename === 'JiraIssueLayoutFieldConfigurationHiddenInLayoutOwner') {
			const { fieldConfigs, layoutOwnerId } = configuration;

			hiddenIn.push({
				layoutOwnerId,
				fieldConfigs: (fieldConfigs || []).map(transformFieldConfig),
			});
		}
	}

	return hiddenIn;
};

const transformContext = (
	context: GraphqlFieldAvailability['context'],
): AvailabilityContext | null => {
	if (context == null) {
		return null;
	}

	const transformedContext: AvailabilityContext = {
		id: context.id,
		issueTypeIds: context.layoutOwnerIds,
	};

	return transformedContext;
};

const transformDisplayConditions = (
	displayConditions: GraphqlFieldAvailability['displayConditions'],
): DisplayConditions | null => {
	if (displayConditions == null) {
		return null;
	}

	const keys = Object.keys(displayConditions);

	const simpleProjectDisplayConditionList = [
		'projectKey',
		'projectType',
		'projectId',
		'projectName',
	];

	const transformedDisplayConditions: DisplayConditions = {
		...displayConditions,
		// for single issue type and project conditions we can evaluate whether the field is hidden
		// on frontend side and provide admins more specific information about field visibility
		isSimpleIssueTypeDisplayCondition: keys.length === 1 && keys[0] === 'issueType',
		isSimpleProjectDisplayCondition:
			keys.length === 1 && simpleProjectDisplayConditionList.includes(keys[0]),
	};

	return transformedDisplayConditions;
};

const transformAvailability = (
	availability?: GraphqlFieldAvailability | null,
): Availability | null => {
	if (availability == null) {
		return null;
	}

	const hiddenIn = transformIsHiddenIn(availability.isHiddenIn);
	const context = transformContext(availability.context);
	const displayConditions = transformDisplayConditions(availability.displayConditions);
	const { isHiddenByAppAccessRules } = availability;

	return { hiddenIn, context, displayConditions, isHiddenByAppAccessRules };
};

/**
 * Transform field data from GraphQL into a SWIFT compatible {@link Field} object.
 */
export const transformField: FieldTransformer = (
	field: GraphqlFieldItem,
	transformers: Transformers,
): Field => {
	const {
		name,
		key,
		externalUuid,
		type,
		custom,
		global,
		description,
		options,
		operations,
		provider,
		availability,
		configuration,
	} = field;

	return {
		name,
		key,
		externalUuid: toFieldUuid(externalUuid || ''),
		type,
		custom,
		global,
		description: description || '',
		options: normalizeOptions(type, options),
		operations: transformFieldOperationsAdapter(operations, transformers),
		provider: transformProvider(provider),
		availability: transformAvailability(availability),
		configuration,
	};
};
